import { BadgeStatus } from '../badge-status';
import { TextTooltip } from '../text-tooltip';
import { Skeleton } from '../ui/skeleton';
import { UploadedImage } from '../uploaded-image';
import { prefetchRoute } from '@/client/confs/page-loaders';
import { api } from '@/client/utils/api';
import { queryClient } from '@/client/utils/query-client';
import { buildEventDetailQueryOptions } from '@/libs/react-sdk/api/use-event-detail';
import { marketDetailQueryOptions } from '@/libs/react-sdk/api/use-market-detail';
import { MarketStatus } from '@/libs/sdk/api';
import { memo } from 'react';
import { useLocation } from 'wouter';

export const MarketItemSkeleton = memo(() => {
  return (
    <div className="p-3 space-x-3 flex items-center min-h-16 w-full">
      <Skeleton className="size-10 flex-shrink-0 rounded-3" />
      <div className="space-y-1 flex-1">
        <Skeleton className="w-full h-4" />
        <Skeleton className="w-[80%] h-4" />
      </div>
    </div>
  );
});
MarketItemSkeleton.displayName = 'MarketItemSkeleton';

export const MarketsSkeleton = ({ len }: { len: number }) => {
  return Array.from({ length: len }).map((_, index) => <MarketItemSkeleton key={`MarketItemSkeleton-${index}`} />);
};

type MarketItemProps = {
  eventId?: string;
  marketAddress: string;
  marketName: string;
  marketPicUrl: string;
  marketStatus?: MarketStatus;
  marketDeadline?: string;
};
export const MarketItem = ({
  eventId,
  marketAddress,
  marketName,
  marketPicUrl,
  marketDeadline,
  marketStatus,
}: MarketItemProps) => {
  const [, navigate] = useLocation();
  // Warm the target route chunk + detail cache on intent signals so the
  // click-through transition commits without suspending.
  const handlePrefetch = () => {
    if (eventId) {
      prefetchRoute('event');
      void queryClient.prefetchQuery(buildEventDetailQueryOptions(api, { eventId }));
    } else {
      prefetchRoute('market');
      void queryClient.prefetchQuery(marketDetailQueryOptions(api, { marketAddress }));
    }
  };
  return (
    <div
      onClick={() => navigate(eventId ? `/event/${eventId}` : `/market/${marketAddress}`)}
      onMouseEnter={handlePrefetch}
      onFocus={handlePrefetch}
      onTouchStart={handlePrefetch}
      className="p-3 space-x-3 flex items-center hover:bg-foreground/4  rounded-3 min-h-16 cursor-pointer"
    >
      <UploadedImage
        fileKey={marketPicUrl}
        alt={marketName}
        width="40"
        height="40"
        className="size-10 flex-shrink-0 rounded-2"
      />
      <TextTooltip tooltip={marketName} className="line-clamp-2 text-left text-foreground font-400 text-sm">
        {marketName}
      </TextTooltip>
      {marketDeadline && marketStatus != undefined && <BadgeStatus deadline={marketDeadline} status={marketStatus} />}
    </div>
  );
};
